/**
 * Alpine.js Home Header Component
 * Holds the profile section map and the status text of the home header, so a save
 * anywhere on the page updates the ring, the carousel and the status line in place.
 */

import { editorBase } from './alpine-editor-base.js';
import { focusOnTransitionEnd } from './focus-on-transition-end.js';

// Editor fields whose header section has a different name.
const SECTION_BY_FIELD = {
    education_desc: 'education',
};

/**
 * The client copy of the !empty() test in ProfileService::getProfileSectionCompletion().
 * PHP treats '' and '0' as empty, and keeps a whitespace-only string.
 */
export function isFilled(value) {
    return value !== null && value !== undefined && value !== '' && value !== '0';
}

/**
 * A section is open when the map carries it and its value is not filled. The seed is
 * parsed JSON, so a value can arrive as something other than a boolean, and
 * nextFillSection scans FILL_CHAIN, which can name a key the map does not carry.
 * Every reader of the map shares this test so they cannot name different sections.
 *
 * @param {Object<string, boolean>} sections
 * @param {string} key
 * @returns {boolean}
 */
export function isOpen(sections, key) {
    return Object.prototype.hasOwnProperty.call(sections, key) && !sections[key];
}

/**
 * Derive the header state from a section map in weight order, the order
 * ProfileService::SECTION_WEIGHTS gives it. `percent` sums the weights of the done
 * sections. `next` is the first open section the carousel offers, so a section with
 * no pill never takes the pill slot.
 *
 * @param {Object<string, boolean>} sections
 * @param {Object<string, number>} [weights]
 * @param {?string[]} [offered] Sections the carousel draws. Null offers every section.
 * @returns {{done: number, total: number, percent: number, next: ?string, open: string[]}}
 */
export function summarizeSections(sections, weights = {}, offered = null) {
    const keys = Object.keys(sections);
    const open = keys.filter((key) => isOpen(sections, key));
    const percent = keys
        .filter((key) => !isOpen(sections, key))
        .reduce((sum, key) => sum + (Number.isFinite(weights[key]) ? weights[key] : 0), 0);
    const next = open.find((key) => offered === null || offered.includes(key)) ?? null;

    return {
        done: keys.length - open.length,
        total: keys.length,
        percent: Math.min(100, Math.max(0, Math.floor(percent))),
        next,
        open,
    };
}

function setSection(sections, section, done) {
    if (!Object.prototype.hasOwnProperty.call(sections, section)) {
        return false;
    }
    sections[section] = done;

    return true;
}

/**
 * Apply one saved editor field to the section map.
 *
 * @returns {boolean} False when the field is not a header section.
 */
export function applyFieldSave(sections, field, value) {
    return setSection(sections, SECTION_BY_FIELD[field] ?? field, isFilled(value));
}

/**
 * Apply a saved interest list. The server counts interests as tags, not as text.
 *
 * @returns {boolean} False when the map has no interests section.
 */
export function applyInterests(sections, tags) {
    return setSection(sections, 'interests', Array.isArray(tags) && tags.length > 0);
}

/**
 * Apply the language list the language modal saved. Level 4 is native, as
 * ProfileService reads it.
 *
 * @param {Object<string, boolean>} sections
 * @param {Array<{level: number, learning: boolean}>} langs
 */
export function applyLanguages(sections, langs) {
    const list = Array.isArray(langs) ? langs : [];
    setSection(sections, 'native_language', list.some((lang) => Number(lang?.level) === 4 && !lang?.learning));
    setSection(sections, 'practice_language', list.some((lang) => Boolean(lang?.learning)));
}

/**
 * Sections the fill sheet offers, in the order it offers them. `description` and
 * `interests` are left out: About needs the profile page and interests open the tag
 * modal, so the chain never lands on either.
 */
export const FILL_CHAIN = ['requests', 'occupation', 'education', 'hobbies', 'music',
    'movies', 'tv', 'books', 'quotes'];

/**
 * The section the sheet offers after `section`: the first one still open after it in
 * chain order, wrapping to the ones before it so nothing open is unreachable.
 *
 * @param {Object<string, boolean>} sections
 * @param {?string} section
 * @returns {?string}
 */
export function nextFillSection(sections, section) {
    const start = FILL_CHAIN.indexOf(section);
    const ordered = start < 0
        ? FILL_CHAIN
        : [...FILL_CHAIN.slice(start + 1), ...FILL_CHAIN.slice(0, start)];

    return ordered.find((key) => isOpen(sections, key)) ?? null;
}

/**
 * The Next button names the field it moves to. A locale whose `welcome_fill_next`
 * drops the `%s` placeholder would otherwise name nothing, so the label is appended.
 */
export function formatFillNext(template, label) {
    if (template.includes('%s')) {
        return template.replace('%s', label).trim();
    }

    return label ? `${template} ${label}` : template;
}

/**
 * Read the header seed. Returns null when it is missing, unparseable, or carries no
 * section map, which are the cases where the client knows nothing about completion.
 *
 * @param {?string} raw
 * @returns {?{sections: Object<string, boolean>, weights: Object<string, number>, offered: ?string[],
 *     status: string, labels: Object<string, string>}}
 */
export function parseSeed(raw) {
    if (!raw) {
        return null;
    }

    let seed;
    try {
        seed = JSON.parse(raw);
    } catch (error) {
        console.warn('Home header seed is not JSON:', error.message);
        return null;
    }

    const sections = seed?.sections;
    if (!sections || typeof sections !== 'object' || Array.isArray(sections)
        || Object.keys(sections).length === 0) {
        return null;
    }
    const isMap = (value) => value && typeof value === 'object' && !Array.isArray(value);

    return {
        sections,
        weights: isMap(seed.weights) ? seed.weights : {},
        offered: Array.isArray(seed.offered) ? seed.offered : null,
        status: typeof seed.status === 'string' ? seed.status : '',
        labels: seed.labels && !Array.isArray(seed.labels) && typeof seed.labels === 'object'
            ? seed.labels
            : {},
    };
}

export function homeHeader() {
    // The sheet mounts each editor in its own x-data, and Alpine gives that editor a
    // scope inheriting this one. A method called from inside it runs with the child as
    // `this`, so an assignment would land on the child and the header would never see
    // it. Every sheet method therefore writes through `root`, the proxy Alpine passes
    // to init(), and keeps its bookkeeping in this closure.
    let root = null;
    // confirmed: the save token that set Saved for the open field, null until a save confirms.
    const sheet = { editor: null, form: null, opener: null, cancelFocus: null, opening: false, confirmed: null };

    return {
        // False until a seed lands. Every binding in the template falls back to the
        // state the server rendered while it is false, so a header that cannot hydrate
        // shows what the page was built with instead of a guess.
        hydrated: false,
        sections: {},
        weights: {},
        // Sections the carousel draws a pill for. Null offers every section.
        offered: null,
        status: '',
        // Sheet titles by section, so the sheet and the Next button name a field the
        // same way the profile page does.
        labels: {},

        // Section the fill sheet is editing, null while it is closed.
        fillField: null,
        fillSaved: false,

        get summary() {
            return summarizeSections(this.sections, this.weights, this.offered);
        },

        get done() {
            return this.summary.done;
        },

        get total() {
            return this.summary.total;
        },

        get percent() {
            return this.summary.percent;
        },

        get next() {
            return this.summary.next;
        },

        get complete() {
            return this.summary.open.length === 0;
        },

        // An open section other than the next one draws as a dashed disc.
        showDisc(section) {
            return isOpen(this.sections, section) && this.next !== section;
        },

        init() {
            root = this;
            const seed = parseSeed(this.$el.dataset.seed);
            if (!seed) {
                // No map means no way to recount a save, so the header stays as rendered.
                return;
            }
            this.sections = seed.sections;
            this.weights = seed.weights;
            this.offered = seed.offered;
            this.status = seed.status;
            this.labels = seed.labels;
            this.hydrated = true;

            this._onFieldSaved = (event) => {
                const { field, value, token } = event.detail;
                if (field === 'status') {
                    this.status = value;
                    return;
                }
                const section = SECTION_BY_FIELD[field] ?? field;
                applyFieldSave(this.sections, field, value);
                // A save that leaves the section open is not an answer, so the sheet
                // keeps its form instead of offering the field again under "Next".
                // sheet.editor is the merged $data proxy, never the editor's `this`, so the save
                // is matched by its token. A save from a sheet closed mid-request carries an older one.
                if (section === root.fillField && isFilled(value) && token && token === sheet.editor?.saveToken) {
                    sheet.confirmed = token;
                    root.fillSaved = true;
                    // x-show queues its reveal frame during the flush, before this frame, so Next and Done show first.
                    root.$nextTick(() => requestAnimationFrame(() => root.focusSavedAction()));
                }
            };
            this._onInterestsUpdated = (event) => {
                applyInterests(this.sections, event.detail?.tags);
            };
            this._onLanguagesUpdated = (event) => {
                applyLanguages(this.sections, event.detail?.allUserLangs);
            };
            window.addEventListener(editorBase.SAVED_EVENT, this._onFieldSaved);
            window.addEventListener('interests-updated', this._onInterestsUpdated);
            window.addEventListener('languages-updated', this._onLanguagesUpdated);
        },

        get fillNext() {
            return this.fillField ? nextFillSection(this.sections, this.fillField) : null;
        },

        get fillLabel() {
            return this.labels[this.fillField] ?? '';
        },

        get fillNextLabel() {
            return this.labels[this.fillNext] ?? '';
        },

        /** `template` is the translated Next label, passed in by the sheet markup. */
        fillNextText(template) {
            return formatFillNext(template, root.fillNextLabel);
        },

        /**
         * Open the one-field sheet. `opener` takes focus back when the sheet closes.
         */
        openFill(section, opener) {
            root.fillField = section;
            root.fillSaved = false;
            sheet.confirmed = null;
            sheet.opener = opener ?? null;
            sheet.opening = true;
        },

        /**
         * Take the editor the sheet just mounted, so every close path can reach its
         * cancel(), and focus its field. `form` is the editor root, which holds Done and Next.
         */
        registerFill(editor, field, form) {
            sheet.editor = editor;
            sheet.form = form ?? null;
            sheet.cancelFocus?.();
            sheet.cancelFocus = null;

            if (!field) {
                return;
            }
            const focus = () => {
                field.focus();
                if (typeof field.setSelectionRange === 'function') {
                    field.setSelectionRange(field.value.length, field.value.length);
                }
            };

            // Only an opening sheet runs a transition. A field swapped in behind an open
            // sheet has none to wait for, and waiting would sit out the fallback delay.
            if (sheet.opening) {
                sheet.opening = false;
                sheet.cancelFocus = focusOnTransitionEnd(field, focus);
            } else {
                focus();
            }
        },

        goNextFill() {
            const next = root.fillNext;
            if (!next) {
                root.closeFill();
                return;
            }
            sheet.cancelFocus?.();
            sheet.cancelFocus = null;
            sheet.editor = null;
            sheet.form = null;
            sheet.confirmed = null;
            root.fillField = next;
            root.fillSaved = false;
        },

        /**
         * After a save, focus Next while a field is still open, else Done. Focus the user
         * moved out of the sheet form stays where they put it.
         */
        focusSavedAction() {
            const form = sheet.form;
            if (!form || !root.fillSaved) {
                return;
            }
            const active = document.activeElement;
            if (active !== null && active !== document.body && !form.contains(active)) {
                return;
            }
            form.querySelector(root.fillNext ? '[data-fill-next]' : '[data-fill-done]')?.focus();
        },

        /**
         * The open editor calls this from an x-effect with its hasChanges and saveToken. An edit
         * after a save clears Saved, and a revert to the confirmed save shows it again.
         */
        fillEdited(changed, token) {
            // Write only: a read of fillSaved here would make the x-effect track it.
            root.fillSaved = !changed && token != null && token === sheet.confirmed;
        },

        /**
         * Every close path lands here: the backdrop, Escape, the header button and Done.
         * The editor's cancel() drops an unsaved draft, and the sheet shell unlocks the
         * page as soon as fillField clears.
         */
        closeFill() {
            sheet.cancelFocus?.();
            sheet.cancelFocus = null;
            sheet.editor?.cancel?.();
            sheet.editor = null;
            sheet.form = null;
            sheet.confirmed = null;
            root.fillField = null;
            root.fillSaved = false;
            root.$nextTick(() => root.restoreFillFocus());
        },

        restoreFillFocus() {
            const opener = sheet.opener;
            sheet.opener = null;

            if (opener?.isConnected && opener.offsetParent !== null) {
                opener.focus();
                return;
            }
            // The pill that opened the sheet is gone once its section is filled, so
            // focus lands on the first one the carousel still offers.
            const pills = [...root.$el.querySelectorAll('[data-fill-pill]')];
            pills.find((pill) => pill.offsetParent !== null)?.focus();
        },

        openInterests() {
            window.dispatchEvent(new CustomEvent('open-interests-modal', { detail: { tags: [] } }));
        },

        /** welcome-box.twig mounts profileLanguageModal() beside the header to hear this. */
        openLanguages() {
            window.dispatchEvent(new CustomEvent('open-language-modal'));
        },

        destroy() {
            sheet.cancelFocus?.();
            if (!this.hydrated) {
                return;
            }
            window.removeEventListener(editorBase.SAVED_EVENT, this._onFieldSaved);
            window.removeEventListener('interests-updated', this._onInterestsUpdated);
            window.removeEventListener('languages-updated', this._onLanguagesUpdated);
        },
    };
}
